<template>
  <div class="record-detail-wrapper" v-loading="loading">
    <!-- 记录基本信息 -->
    <div class="record-info-section" v-if="recordInfo">
      <h3>基本信息</h3>
      <el-form label-width="120px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="记录ID:">
              <span>{{ recordInfo.record_id }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="用户ID:">
              <span>{{ recordInfo.user_id }}</span>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="游戏ID:">
              <span>{{ recordInfo.game_id }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="当前节点ID:">
              <span>{{ recordInfo.current_node_id }}</span>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="创建时间:">
          <span>{{ formatDate(recordInfo.create_time) }}</span>
        </el-form-item>
      </el-form>
    </div>

    <el-divider />

    <!-- 对话记录列表 -->
    <div class="dialogue-section">
      <h3>对话记录 ({{ dialogues.length }} 条)</h3>
      
      <div v-if="dialogues.length === 0 && !loading" class="empty-dialogues">
        <i class="el-icon-chat-line-square" style="font-size: 64px; color: #ddd; margin-bottom: 16px;"></i>
        <p>暂无对话记录</p>
      </div>

      <div v-else class="dialogue-list">
        <div v-for="dialogue in dialogues" :key="dialogue.dialog_id" 
             class="dialogue-item">
          
          <!-- 对话头部信息 -->
          <div class="dialogue-header">
            <div class="speaker-info">
              <el-avatar 
                v-if="dialogue.sender.avatar" 
                :src="dialogue.sender.avatar" 
                :size="40"
                class="speaker-avatar">
              </el-avatar>
              <el-avatar v-else :size="40" class="speaker-avatar">
                {{ dialogue.sender.name.charAt(0) }}
              </el-avatar>
              
              <div class="speaker-details">
                <div class="speaker-name">{{ dialogue.sender.name }}</div>
                <div class="speaker-type">
                  <el-tag size="mini" :type="getRoleTypeColor(dialogue.sender.role_type)">
                    {{ dialogue.sender.role_type }}
                  </el-tag>
                </div>
              </div>
            </div>

            <div class="dialogue-meta">
              <div class="node-info">
                <el-tag type="info" size="mini">
                  {{ dialogue.node_title }} ({{ dialogue.node_order }})
                </el-tag>
              </div>
              <div class="dialogue-time">
                {{ formatDate(dialogue.create_time) }}
              </div>
            </div>
          </div>

          <!-- 对话内容 -->
          <div class="dialogue-content">
            <div class="dialogue-text">
              {{ dialogue.text }}
            </div>
            
            <!-- 语音播放器 -->
            <div v-if="dialogue.voice_url" class="voice-section">
              <el-button 
                size="mini" 
                type="primary" 
                icon="el-icon-video-play"
                @click="playAudio(dialogue.dialog_id)"
                :loading="playingAudioId === dialogue.dialog_id">
                播放语音
              </el-button>
              <audio 
                :ref="`audio_${dialogue.dialog_id}`"
                :src="dialogue.voice_url"
                @ended="onAudioEnded"
                style="display: none;">
              </audio>
            </div>
          </div>

          <!-- 对话底部信息 -->
          <div class="dialogue-footer">
            <div class="dialogue-type">
              <el-tag size="mini" type="success">{{ dialogue.type }}</el-tag>
            </div>
            <div class="dialogue-id">
              ID: {{ dialogue.dialog_id }}
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import service from '@/utils/request'
import { Game } from "@/api/game";

interface DialogueItem {
  dialog_id: number
  node_id: number
  node_title: string
  node_order: number
  text: string
  voice_url: string | null
  create_time: string
  type: string
  sender: {
    role_id: number
    name: string
    avatar: string
    role_type: string
  }
}

interface RecordInfo {
  record_id: number
  user_id: string
  game_id: number
  current_node_id: number
  create_time: string
}

@Component
export default class RecordDetailContent extends Vue {
  @Prop({ required: true }) readonly recordId!: number
  @Prop({ required: true }) readonly game!: Game

  dialogues: DialogueItem[] = []
  recordInfo: RecordInfo | null = null
  loading = false
  playingAudioId: number | null = null

  @Watch('recordId', { immediate: true })
  onRecordIdChange(newRecordId: number) {
    if (newRecordId) {
      this.fetchRecordDetail()
    }
  }

  async fetchRecordDetail() {
    if (!this.game || !this.game.id) {
      console.error('Game对象未定义或缺少id')
      return
    }
    
    this.loading = true
    try {
      const response = await service.get(`/api/record_log/${this.recordId}?game_id=${this.game.id}`)
      if (response.data.message === 'success') {
        this.dialogues = response.data.data
        this.recordInfo = response.data.record_info
      }
    } catch (error) {
      console.error('获取记录详情失败:', error)
      this.$message.error('获取记录详情失败')
    } finally {
      this.loading = false
    }
  }

  formatDate(dateString: string) {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('zh-CN')
  }

  getRoleTypeColor(roleType: string) {
    const colorMap: { [key: string]: string } = {
      'user': 'primary',
      'ai': 'success',
      'system': 'warning',
      'assistant': 'info'
    }
    return colorMap[roleType] || ''
  }

  playAudio(dialogId: number) {
    this.playingAudioId = dialogId
    const audioRef = this.$refs[`audio_${dialogId}`] as HTMLAudioElement[]
    if (audioRef && audioRef[0]) {
      audioRef[0].play().catch((error) => {
        console.error('音频播放失败:', error)
        this.$message.error('音频播放失败')
        this.playingAudioId = null
      })
    }
  }

  onAudioEnded() {
    this.playingAudioId = null
  }
}
</script>

<style scoped>
.record-detail-wrapper {
  padding: 20px 0;
}

.record-info-section {
  margin-bottom: 30px;
}

.record-info-section h3 {
  color: #303133;
  margin-bottom: 20px;
  font-size: 18px;
}

.dialogue-section h3 {
  color: #303133;
  margin-bottom: 20px;
  font-size: 18px;
}

.empty-dialogues {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.dialogue-list {
  max-height: 500px;
  overflow-y: auto;
}

.dialogue-item {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  border-left: 4px solid #409EFF;
}

.dialogue-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.speaker-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.speaker-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.speaker-name {
  font-weight: 600;
  color: #303133;
  font-size: 14px;
}

.dialogue-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.dialogue-time {
  font-size: 12px;
  color: #909399;
}

.dialogue-content {
  margin-bottom: 12px;
}

.dialogue-text {
  color: #606266;
  line-height: 1.6;
  margin-bottom: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}

.voice-section {
  margin-top: 8px;
}

.dialogue-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #909399;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .record-detail-wrapper {
    padding: 10px 0;
  }
  
  .dialogue-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .dialogue-meta {
    align-items: flex-start;
  }
  
  .dialogue-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>